/**
 * Statistics hub – game-specific stats (rankings, points/goals/runs, recent results).
 * Lazy-loaded: rankings first (speed), then recent matches when section is in view.
 * Cache-first; fresh data matters when matches are completed (cache invalidated then).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProfileLink from '../components/ProfileLink';
import { useGame } from '../contexts/GameContext';
import { getStatisticsRankings, getStatisticsRecentMatches } from '../api/readApi';
import { GAMES } from '../constants/games';
import { formatDate } from '../utils/dateFormat';
import './Statistics.css';

/** Game-specific stats display: section titles, column labels, and short description. */
function getStatsConfig(game) {
  if (!game) return getStatsConfig(GAMES[0]);
  const id = game.id || 'table-tennis';
  const pts = game.pointLabelPlural || 'Points';
  const matchPlural = game.matchLabelPlural || 'Matches';

  const configs = {
    'table-tennis': {
      rankingTitle: 'Rankings',
      rankingDescription: 'Rally points and set wins',
      matLabel: 'Mat',
      ptsForLabel: 'Pts For',
      ptsAgainstLabel: 'Pts Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Most points',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see rankings.',
      emptyRecent: 'No completed matches yet.',
    },
    badminton: {
      rankingTitle: 'Rankings',
      rankingDescription: 'Shuttle points and games',
      matLabel: 'Mat',
      ptsForLabel: 'Pts For',
      ptsAgainstLabel: 'Pts Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Most points',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see rankings.',
      emptyRecent: 'No completed matches yet.',
    },
    football: {
      rankingTitle: 'Standings',
      rankingDescription: 'Goals and matches',
      matLabel: 'Mat',
      ptsForLabel: 'Goals For',
      ptsAgainstLabel: 'Goals Against',
      diffLabel: 'GD',
      topSectionTitle: 'Top scorers',
      recentSectionTitle: 'Recent matches',
      emptyRankings: 'Play some matches to see standings.',
      emptyRecent: 'No completed matches yet.',
    },
    cricket: {
      rankingTitle: 'Rankings',
      rankingDescription: 'Runs and matches',
      matLabel: 'Mat',
      ptsForLabel: 'Runs For',
      ptsAgainstLabel: 'Runs Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Top run scorers',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see rankings.',
      emptyRecent: 'No completed matches yet.',
    },
    basketball: {
      rankingTitle: 'Standings',
      rankingDescription: 'Points and games',
      matLabel: 'Mat',
      ptsForLabel: 'Pts For',
      ptsAgainstLabel: 'Pts Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Top scorers',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see standings.',
      emptyRecent: 'No completed matches yet.',
    },
    volleyball: {
      rankingTitle: 'Rankings',
      rankingDescription: 'Points and sets',
      matLabel: 'Mat',
      ptsForLabel: 'Pts For',
      ptsAgainstLabel: 'Pts Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Most points',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see rankings.',
      emptyRecent: 'No completed matches yet.',
    },
    tennis: {
      rankingTitle: 'Rankings',
      rankingDescription: 'Points and sets',
      matLabel: 'Mat',
      ptsForLabel: 'Pts For',
      ptsAgainstLabel: 'Pts Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Most points',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see rankings.',
      emptyRecent: 'No completed matches yet.',
    },
    pickleball: {
      rankingTitle: 'Rankings',
      rankingDescription: 'Points and games',
      matLabel: 'Mat',
      ptsForLabel: 'Pts For',
      ptsAgainstLabel: 'Pts Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Most points',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see rankings.',
      emptyRecent: 'No completed matches yet.',
    },
    squash: {
      rankingTitle: 'Rankings',
      rankingDescription: 'Points and games',
      matLabel: 'Mat',
      ptsForLabel: 'Pts For',
      ptsAgainstLabel: 'Pts Against',
      diffLabel: 'Diff',
      topSectionTitle: 'Most points',
      recentSectionTitle: 'Recent results',
      emptyRankings: 'Play some matches to see rankings.',
      emptyRecent: 'No completed matches yet.',
    },
  };

  const fallback = {
    rankingTitle: 'Rankings',
    rankingDescription: `${pts} and ${matchPlural.toLowerCase()}`,
    matLabel: 'Mat',
    ptsForLabel: `${pts} For`,
    ptsAgainstLabel: `${pts} Against`,
    diffLabel: 'Diff',
    topSectionTitle: `Most ${pts.toLowerCase()}`,
    recentSectionTitle: 'Recent results',
    emptyRankings: 'Play some matches to see rankings.',
    emptyRecent: 'No completed matches yet.',
  };

  return configs[id] || fallback;
}

function formatScoreSummary(scores) {
  if (!scores) return '–';
  const games = [];
  if (scores.game1) games.push(`${scores.game1.player1 ?? 0}-${scores.game1.player2 ?? 0}`);
  if (scores.game2) games.push(`${scores.game2.player1 ?? 0}-${scores.game2.player2 ?? 0}`);
  if (scores.game3) games.push(`${scores.game3.player1 ?? 0}-${scores.game3.player2 ?? 0}`);
  return games.join(', ');
}

export default function Statistics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { gameId, games, setGameId } = useGame();
  const [rankingsSingles, setRankingsSingles] = useState([]);
  const [rankingsDoubles, setRankingsDoubles] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(true);

  const gamesToShow = useMemo(() => (Array.isArray(games) && games.length > 0 ? games : GAMES), [games]);
  const currentGame = useMemo(() => GAMES.find((g) => g.id === gameId) || GAMES[0], [gameId]);

  useEffect(() => {
    const game = searchParams.get('game');
    if (game && GAMES.some((g) => g.id === game)) setGameId(game);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when URL query changes
  }, [searchParams]);

  // Load statistics: cache-first (browser + functions); rankings and recent matches in parallel
  useEffect(() => {
    let cancelled = false;
    setLoadingRankings(true);
    setRankingsSingles([]);
    setRankingsDoubles([]);
    setRecentMatches([]);
    Promise.all([
      getStatisticsRankings(gameId),
      getStatisticsRecentMatches(gameId),
    ])
      .then(([rankingsData, recentData]) => {
        if (cancelled) return;
        setRankingsSingles(rankingsData?.rankingsSingles ?? []);
        setRankingsDoubles(rankingsData?.rankingsDoubles ?? []);
        setRecentMatches(recentData?.recentMatches ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setRankingsSingles([]);
          setRankingsDoubles([]);
          setRecentMatches([]);
          console.error('Statistics fetch error:', err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRankings(false);
      });
    return () => { cancelled = true; };
  }, [gameId]);

  const config = useMemo(() => getStatsConfig(currentGame), [currentGame]);

  return (
    <div className={`statistics-page statistics-page--${gameId}`}>
      <div className="statistics-header">
        <h1 className="statistics-title">Statistics</h1>
        <p className="statistics-subtitle">
          {currentGame.icon} {currentGame.name} — {config.rankingDescription}
        </p>
        <div className="statistics-sport-links">
          {gamesToShow.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setSearchParams({ game: g.id })}
              className={`statistics-sport-tab ${g.id === gameId ? 'active' : ''}`}
            >
              {g.icon} {g.name}
            </button>
          ))}
        </div>
      </div>

      {/* Singles rankings – game-specific labels */}
      <section className="statistics-section statistics-section--ranking statistics-section--singles" aria-labelledby="statistics-singles-title">
        <h2 className="statistics-section-title" id="statistics-singles-title">
          <span className="statistics-type-badge">Singles</span>
          <span>{config.rankingTitle}</span>
        </h2>
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr>
                <th className="col-rank">#</th>
                <th className="col-player">Player</th>
                <th className="col-num">{config.matLabel}</th>
                <th className="col-num">Won</th>
                <th className="col-num">Lost</th>
                <th className="col-num">{config.ptsForLabel}</th>
                <th className="col-num">{config.ptsAgainstLabel}</th>
                <th className="col-num">{config.diffLabel}</th>
                <th className="col-num">Win %</th>
              </tr>
            </thead>
            <tbody>
              {loadingRankings ? (
                <tr><td colSpan={9} className="stats-table-empty statistics-loading-inline">Loading rankings…</td></tr>
              ) : rankingsSingles.length === 0 ? (
                <tr><td colSpan={9} className="stats-table-empty">{config.emptyRankings}</td></tr>
              ) : (
                rankingsSingles.map((row, index) => (
                  <tr key={row.playerId} className={index < 3 ? `top-row rank-${index + 1}` : ''}>
                    <td className="col-rank">{index + 1}</td>
                    <td className="col-player">
                      <ProfileLink userId={row.playerId} className="stats-player-link">{row.playerName}</ProfileLink>
                    </td>
                    <td className="col-num">{row.mat}</td>
                    <td className="col-num col-won">{row.won}</td>
                    <td className="col-num col-lost">{row.lost}</td>
                    <td className="col-num">{row.ptsFor}</td>
                    <td className="col-num">{row.ptsAgainst}</td>
                    <td className="col-num col-diff">{row.diff >= 0 ? `+${row.diff}` : row.diff}</td>
                    <td className="col-num">{row.winRate}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Doubles rankings – game-specific labels */}
      <section className="statistics-section statistics-section--ranking statistics-section--doubles" aria-labelledby="statistics-doubles-title">
        <h2 className="statistics-section-title" id="statistics-doubles-title">
          <span className="statistics-type-badge">Doubles</span>
          <span>{config.rankingTitle}</span>
        </h2>
        <div className="stats-table-wrap">
          <table className="stats-table">
            <thead>
              <tr>
                <th className="col-rank">#</th>
                <th className="col-player">Player / Team</th>
                <th className="col-num">{config.matLabel}</th>
                <th className="col-num">Won</th>
                <th className="col-num">Lost</th>
                <th className="col-num">{config.ptsForLabel}</th>
                <th className="col-num">{config.ptsAgainstLabel}</th>
                <th className="col-num">{config.diffLabel}</th>
                <th className="col-num">Win %</th>
              </tr>
            </thead>
            <tbody>
              {loadingRankings ? (
                <tr><td colSpan={9} className="stats-table-empty statistics-loading-inline">Loading…</td></tr>
              ) : rankingsDoubles.length === 0 ? (
                <tr><td colSpan={9} className="stats-table-empty">{config.emptyRankings}</td></tr>
              ) : (
                rankingsDoubles.map((row, index) => (
                  <tr key={row.pairKey || row.playerId} className={index < 3 ? `top-row rank-${index + 1}` : ''}>
                    <td className="col-rank">{index + 1}</td>
                    <td className="col-player">
                      {row.playerIds?.length === 2 && row.playerNames?.length === 2 ? (
                        <>
                          <ProfileLink userId={row.playerIds[0]} className="stats-player-link">{row.playerNames[0]}</ProfileLink>
                          <span className="doubles-sep"> & </span>
                          <ProfileLink userId={row.playerIds[1]} className="stats-player-link">{row.playerNames[1]}</ProfileLink>
                          {row.teamName && <span className="doubles-team-name"> ({row.teamName})</span>}
                        </>
                      ) : (
                        <>
                          <ProfileLink userId={row.playerId} className="stats-player-link">{row.playerName}</ProfileLink>
                          {row.teamName && <span className="doubles-team-name"> ({row.teamName})</span>}
                        </>
                      )}
                    </td>
                    <td className="col-num">{row.mat}</td>
                    <td className="col-num col-won">{row.won}</td>
                    <td className="col-num col-lost">{row.lost}</td>
                    <td className="col-num">{row.ptsFor}</td>
                    <td className="col-num">{row.ptsAgainst}</td>
                    <td className="col-num col-diff">{row.diff >= 0 ? `+${row.diff}` : row.diff}</td>
                    <td className="col-num">{row.winRate}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent results */}
      <section className="statistics-section statistics-section--recent">
        <h2 className="statistics-section-title">{config.recentSectionTitle}</h2>
        <div className="stats-table-wrap">
          <table className="stats-table stats-table-recent">
            <thead>
              <tr>
                <th className="col-date">Date</th>
                <th className="col-result">Result</th>
                <th className="col-score">Score</th>
              </tr>
            </thead>
            <tbody>
              {loadingRankings ? (
                <tr><td colSpan={3} className="stats-table-empty statistics-loading-inline">Loading recent results…</td></tr>
              ) : recentMatches.length === 0 ? (
                <tr><td colSpan={3} className="stats-table-empty">{config.emptyRecent}</td></tr>
              ) : (
                recentMatches.map((m) => (
                  <tr key={m.id}>
                    <td className="col-date">{m.scheduledAt ? formatDate(m.scheduledAt) : '–'}</td>
                    <td className="col-result">
                      <span className="result-winner">{m.winnerName}</span>
                      <span className="result-vs"> def. </span>
                      <span className="result-loser">{m.loserName}</span>
                    </td>
                    <td className="col-score">{formatScoreSummary(m.scores)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="statistics-links">
        <h2 className="statistics-section-title">More</h2>
        <ul className="statistics-link-list">
          {gamesToShow.map((game) => (
            <li key={game.id}>
              <Link to={`/sport/${game.id}`}>{game.icon} {game.name}</Link>
              <span className="statistics-link-meta"> — <Link to={`/sport/${game.id}/match`}>matches</Link></span>
            </li>
          ))}
        </ul>
        <p className="statistics-desc">
          Player profile: <code>/player/:userId</code>. Tournaments: <code>/sport/:gameId/tournament/:tournamentId</code>.
        </p>
      </section>
    </div>
  );
}
